import { useId, useState } from 'react'
import type { PinVault } from '@application/ports.ts'
import { childPinKey } from '@application/ports.ts'
import type { Member } from '@domain/state.ts'
import { es } from '../strings/es.ts'
import { PinField } from '../shell/PinField.tsx'

/**
 * A child's own door, and deliberately a much smaller one than the parents'.
 *
 * What it is for: a brother walking into his brother's screen and ticking off
 * half his afternoon as a joke. That is the whole threat model, and it is a
 * real one in this house.
 *
 * What it is not for: keeping anybody out of anything that matters. So there is
 * no lockout here. A child who mistypes their own PIN four times is a child who
 * forgot it, not an attacker, and locking them out of their own routine for
 * minutes would punish exactly the wrong person. The wrong number just says so
 * and lets them try again; a parent can clear it from the settings.
 */
export function ChildPinGate({
  member,
  vault,
  onOpen,
  onCancel,
}: {
  member: Member
  vault: PinVault
  onOpen: () => void
  onCancel: () => void
}) {
  const [pin, setPin] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const fieldId = useId()

  const submit = async (): Promise<void> => {
    setBusy(true)
    try {
      if (await vault.verify(pin, childPinKey(member.id))) {
        onOpen()
      } else {
        setError(es.childPin.wrong)
        setPin('')
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="mx-auto flex min-h-dvh max-w-sm flex-col justify-center px-5">
      <h1 className="mb-2 flex items-center gap-3 text-3xl font-extrabold tracking-tight">
        <span aria-hidden="true">{member.emoji ?? '🙂'}</span>
        {member.name}
      </h1>
      <p className="text-muted mt-0 mb-6 text-sm">{es.childPin.explain}</p>

      <PinField id={fieldId} label={es.childPin.enter} value={pin} onChange={setPin} />

      {error ? (
        <p role="alert" className="text-coral mt-0 mb-4 text-sm font-bold">
          {error}
        </p>
      ) : null}

      <button
        type="button"
        disabled={busy || pin.length < 4}
        onClick={() => {
          void submit()
        }}
        className="bg-teal w-full rounded-xl px-6 py-4 text-lg font-extrabold text-white disabled:opacity-40"
        style={{ background: member.accent }}
      >
        {es.childPin.submit}
      </button>
      <button
        type="button"
        onClick={onCancel}
        className="text-muted mx-auto mt-6 text-sm font-bold underline underline-offset-4"
      >
        {es.childPin.cancel}
      </button>
    </div>
  )
}
